import { Link } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible'
import {
  type ActiveTourDetail,
  type TourLoadCheck,
  type TourLoadCheckVehicleLoad,
  useTourLoadChecksQuery,
} from '~/lib/logistics-operations-query'
import { cn } from '~/lib/utils'
import { withoutDetailTab } from '~/routes/_authenticated/logistics/-search-schema'
import { usePermissionGuard } from '~/utils/rbac'
import { DETAIL_PANEL_FOOTER_CLASS } from './detail-panel-frame'
import { LOGISTICS_SIMULATION_READ } from './logistics-rights'
import { QueryError } from './logistics-shared'
import { Gauge, SectionTitle } from './logistics-ui'
import { tourPlaceLabelsOf } from './operations-detail'
import type { RunWarningLevel } from './run-warnings'
import {
  RunWarningDot,
  RunWarningIcon,
  RunWarningList,
  runWarningBadgeClassOf,
} from './run-warnings-ui'
import { placeLabelOrRemoved, type ScenarioPlace } from './scenario-view'
import {
  isLoadOverCapacity,
  loadDayLabelOf,
  loadFillPercentOf,
  loadSlotsLabelOf,
  loadVehicleLabelOf,
  loadWeightLabelOf,
  TOUR_LOAD_DOT_LABELS,
  TOUR_LOAD_LEVEL_LABELS,
  type TourLoadAlert,
  tourLoadAlertLabelOf,
  tourLoadAlertsOf,
  tourLoadAlertTargetOf,
  tourLoadChecksOf,
  tourLoadSummaryOf,
  formatLoadDay,
} from './tour-load-checks'

/** Pastille d'une tournée validée : « à redécouper » plutôt que le « bloquant » d'une simulation. */
export function TourLoadDot({ level }: { level: RunWarningLevel }) {
  return <RunWarningDot level={level} label={TOUR_LOAD_DOT_LABELS[level]} />
}

/** Gabarit commun des badges de charge (« À redécouper », « À vérifier », « Tient »). */
const LOAD_BADGE_CLASS = 'h-6.5 shrink-0 rounded-md border-transparent px-1.5 text-xs font-semibold'

/** Statut de charge d'une tournée ou d'un jour, aux couleurs des avertissements de #975. */
export function TourLoadBadge({
  level,
  className,
}: {
  level: RunWarningLevel
  className?: string
}) {
  return (
    <Badge
      variant="outline"
      className={cn(LOAD_BADGE_CLASS, 'gap-1', runWarningBadgeClassOf(level), className)}
    >
      <RunWarningIcon level={level} className="size-3.5" />
      {TOUR_LOAD_LEVEL_LABELS[level]}
    </Badge>
  )
}

/** Tournées en alerte, partagées par la pastille de l'onglet et les lignes de la liste. */
export const useTourLoadAlerts = (enabled = true) => {
  const query = useTourLoadChecksQuery(enabled)
  const alerts = useMemo(() => tourLoadAlertsOf(query.data?.checks ?? []), [query.data])
  return { alerts, error: query.error }
}

/** Jours de départ recalculés d'une tournée, sur la même requête que les alertes. */
export const useTourLoadChecks = (tourId: number) => {
  const query = useTourLoadChecksQuery()
  const checks = useMemo(
    () => tourLoadChecksOf(query.data?.checks ?? [], tourId),
    [query.data, tourId],
  )
  return { query, checks }
}

/**
 * Bandeau de la liste des tournées actives : les tournées validées qui ne tiennent plus (rouge) ou
 * sont à vérifier (orange) sur les 3 semaines à venir. Toutes, sans les filtres de la liste (l'API
 * n'en a pas), dont celles qui n'entrent en vigueur que plus tard : elles ne sont pas dans la liste,
 * leur lien mène au simulateur. Replié par défaut ; rien sans alerte.
 */
export function TourLoadAlertsNotice({
  alerts,
  error,
  isTourActive,
}: {
  alerts: readonly TourLoadAlert[]
  error: unknown
  /** La tournée est-elle en vigueur ? `undefined` tant que ce n'est pas connu. */
  isTourActive: (tourId: number) => boolean | undefined
}) {
  const [open, setOpen] = useState(false)
  const summary = tourLoadSummaryOf(alerts)
  if (error) {
    return (
      <div className="relative px-2">
        <QueryError error={error} label="Charge des tournées indisponible" />
      </div>
    )
  }
  if (summary === null) return null
  const level = alerts[0]!.level
  return (
    <div className="relative px-2">
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className={cn('rounded-xl text-sm', runWarningBadgeClassOf(level))}
      >
        <CollapsibleTrigger className="flex w-full cursor-pointer items-start gap-2 rounded-xl p-3 text-left focus-visible:outline-2 focus-visible:outline-primary">
          <RunWarningIcon level={level} className="mt-0.5" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-semibold">Charge des 3 prochaines semaines</span>
            <span className="text-xs">{summary}</span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn('mt-0.5 size-4 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul
            className="flex flex-col gap-1 px-2 pb-2"
            aria-label="Tournées à redécouper ou à vérifier"
          >
            {alerts.map((alert) => (
              <li key={alert.tour.id}>
                <TourLoadAlertLink alert={alert} isActive={isTourActive(alert.tour.id)} />
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

const ALERT_LINK_CLASS =
  'flex items-center gap-2 rounded-lg bg-background-100/70 px-2 py-1.5 text-xs text-primary transition-colors hover:bg-background-100 focus-visible:outline-2 focus-visible:outline-primary'

/** Ligne d'une tournée en alerte, et son lien (`tourLoadAlertTargetOf`). */
function TourLoadAlertLink({
  alert,
  isActive,
}: {
  alert: TourLoadAlert
  isActive: boolean | undefined
}) {
  const canReadSimulations = usePermissionGuard(LOGISTICS_SIMULATION_READ).authorized
  const target = tourLoadAlertTargetOf(isActive, canReadSimulations)
  const content = (
    <>
      <TourLoadDot level={alert.level} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-semibold" title={alert.tour.name}>
          {alert.tour.name}
        </span>
        <span className="text-sage-500">
          {tourLoadAlertLabelOf(alert)}, dès le {formatLoadDay(alert.days[0]!.departureDate)}
          {isActive === false && ' · pas encore en vigueur'}
        </span>
      </span>
    </>
  )
  if (target === 'detail') {
    return (
      <Link
        from="/logistics/"
        to="/logistics/operations/$tourId"
        params={{ tourId: String(alert.tour.id) }}
        search={(prev) => ({ ...prev, detailTab: 'charge' as const })}
        className={ALERT_LINK_CLASS}
      >
        {content}
      </Link>
    )
  }
  if (target === 'simulation') {
    return (
      <Link
        from="/logistics/"
        to="/logistics/simulations/$simulationId"
        params={{ simulationId: String(alert.tour.simulationId) }}
        search={toSimulator}
        className={ALERT_LINK_CLASS}
        title="Ouvrir sa simulation"
      >
        {content}
      </Link>
    )
  }
  return <div className={cn(ALERT_LINK_CLASS, 'hover:bg-background-100/70')}>{content}</div>
}

/**
 * Recherche d'un lien vers la simulation d'une tournée : l'onglet Simulateur s'allume, comme depuis
 * la carte (`store-map-popup.tsx`), et l'onglet du détail d'une tournée n'y passe pas.
 */
const toSimulator = <TSearch extends { detailTab?: unknown }>(search: TSearch) => ({
  ...withoutDetailTab(search),
  tab: 'simulator' as const,
})

/**
 * Onglet « Charge » du détail d'une tournée en vigueur (#982) : chacun de ses jours de départ des
 * 3 semaines à venir, recalculé chaque nuit avec la projection du réappro, la charge de chaque
 * camion et les avertissements du jour ; puis l'accès au simulateur pour la redécouper.
 */
export function TourLoadTab({ tourId, detail }: { tourId: number; detail: ActiveTourDetail }) {
  const { query, checks } = useTourLoadChecks(tourId)
  const canReadSimulations = usePermissionGuard(LOGISTICS_SIMULATION_READ).authorized
  const labels = useMemo(() => tourPlaceLabelsOf(detail.segments), [detail.segments])
  const placeLabel = (place: ScenarioPlace) => placeLabelOrRemoved(labels, place)
  const hasRed = checks.some((check) => check.level === 'red')

  return (
    <>
      <div className="flex flex-col gap-2">
        <SectionTitle
          title="Charge des 3 prochaines semaines"
          inlineRule
          compact
          titleClassName="whitespace-nowrap text-primary-700"
        />
        <p className="text-xs leading-4 text-sage-500">
          Recalculée chaque nuit avec la projection du réappro. En rouge, la tournée ne tient plus
          ce jour-là : à redécouper avant la livraison. En orange : à vérifier.
        </p>
      </div>

      <QueryError error={query.error} label="Charge de la tournée indisponible" />
      {query.isLoading && <p className="p-2 text-sm text-sage-500">Chargement…</p>}
      {query.isSuccess && checks.length === 0 && (
        <p className="rounded-2xl bg-sidebar p-3 text-sm text-sage-500">
          Aucun jour de départ recalculé dans les 3 semaines à venir.
        </p>
      )}

      {checks.length > 0 && (
        <ol className="flex flex-col gap-2" aria-label="Charge par jour de départ">
          {checks.map((check) => (
            <TourLoadDay key={check.id} check={check} placeLabel={placeLabel} />
          ))}
        </ol>
      )}

      {canReadSimulations && (
        <div className={DETAIL_PANEL_FOOTER_CLASS}>
          <Button
            nativeButton={false}
            render={
              <Link
                from="/logistics/operations/$tourId"
                to="/logistics/simulations/$simulationId"
                params={{ simulationId: String(detail.id) }}
                search={toSimulator}
              />
            }
            className={cn(
              'h-12.5 w-full rounded-2xl text-sm font-semibold',
              hasRed
                ? 'bg-primary-600 text-sidebar hover:bg-primary-700'
                : 'border border-primary-600 bg-background-100 text-primary-600 hover:bg-primary-50',
            )}
          >
            Redécouper dans le simulateur
          </Button>
        </div>
      )}
    </>
  )
}

/** Un jour de départ : la date, son statut, la charge de chaque camion, puis ses avertissements. */
function TourLoadDay({
  check,
  placeLabel,
}: {
  check: TourLoadCheck
  placeLabel: (place: ScenarioPlace) => string
}) {
  return (
    <li className="flex flex-col gap-2 rounded-2xl bg-sidebar p-3">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-sm leading-4 font-semibold text-primary-700 first-letter:uppercase">
          {loadDayLabelOf(check)}
        </span>
        {check.level === null ? (
          <Badge variant="outline" className={cn(LOAD_BADGE_CLASS, 'bg-primary-100 text-primary')}>
            Tient
          </Badge>
        ) : (
          <TourLoadBadge level={check.level} />
        )}
      </div>
      {check.loads.map((load) => (
        <TourVehicleLoadLine key={load.vehicleId} load={load} />
      ))}
      <RunWarningList warnings={check.diagnostics} placeLabel={placeLabel} />
    </li>
  )
}

/**
 * Charge d'un camion au départ : emplacements de pali (jauge) et poids sur sa charge utile, dans la
 * jauge de l'onglet Contraintes.
 */
function TourVehicleLoadLine({ load }: { load: TourLoadCheckVehicleLoad }) {
  const over = isLoadOverCapacity(load)
  const weight = loadWeightLabelOf(load)
  const vehicle = loadVehicleLabelOf(load)
  return (
    <Gauge
      label={vehicle}
      value={weight === null ? loadSlotsLabelOf(load) : `${loadSlotsLabelOf(load)} · ${weight}`}
      over={over}
      parts={[
        { percent: loadFillPercentOf(load), className: over ? 'bg-destructive' : 'bg-primary-500' },
      ]}
      meterLabel={`Remplissage de ${vehicle}`}
    />
  )
}

/**
 * Libellé d'un onglet suivi de la pastille de la charge : l'onglet Charge du détail (le plus grave
 * de ses jours), la Gestion opérationnelle de la nav (la tournée la plus en alerte).
 */
export function TourLoadTabLabel({
  label,
  level,
}: {
  label: ReactNode
  level: RunWarningLevel | null
}) {
  return level === null ? (
    label
  ) : (
    <span className="flex items-center gap-1.5">
      {label}
      <TourLoadDot level={level} />
    </span>
  )
}
